import {
  File02Icon,
  CheckmarkSquare02Icon
} from "@hugeicons-pro/core-solid-rounded";
import { SquareLock01Icon } from "@hugeicons-pro/core-solid-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import { useQuery } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import moment from "moment";
import { useMemo } from "react";
import { Link } from "react-router-dom";
import { LiteTextEditor } from "shared/app/Editor/LiteTextEditor";
import { Avatar } from "shared/components/Avatar";
import { cx } from "shared/utils";

interface NotificationCardProps {
  authorName: string;
  readAt: string | null;
  richText?: any;
  type: string;
  url: string;
  metadata: any;
  createAt: string;
}

const mentionTypeMap = {
  mentioned_in_application_notes: {
    title: "Mention in application notes",
    format: "for",
    icon: File02Icon
  },
  mentioned_in_tasks: {
    title: "Mention in a task title",
    format: "",
    icon: CheckmarkSquare02Icon
  }
};

export const NotificationCard = (props: NotificationCardProps) => {
  const { authorName, readAt, richText, type, url, metadata, createAt } = props;

  const { data: application, isLoading: isApplicationLoading } = useQuery({
    queryKey: ["application", metadata.application_id],
    queryFn: () =>
      instance(`/portal/v1/applications/${metadata.application_id}`).then(
        ({ data }) => data
      ),
    enabled: Boolean(metadata.application_id)
  });

  const { data: task, isLoading: isTaskLoading } = useQuery({
    queryKey: ["task", metadata.task_id],
    queryFn: () =>
      instance(`/portal/v1/tasks/${metadata.task_id}`).then(({ data }) => data),
    enabled: Boolean(metadata.task_id)
  });

  const subjectName = useMemo(() => {
    if (type === "mentioned_in_application_notes") {
      if (!application)
        return "application in a property you don’t have access to";

      return (
        mentionTypeMap[type].format +
        " " +
        application.applicants
          .map((applicant) => applicant.first_name?.trim())
          .join(", ")
      );
    }
    return "";
  }, [application, task]);

  const hasAccess = useMemo(() => {
    if (type === "mentioned_in_application_notes") return Boolean(application);
    if (type === "mentioned_in_tasks") return Boolean(task);
    return true;
  }, [application, type]);

  if (isApplicationLoading || isTaskLoading) return null;

  return (
    <Link
      to={hasAccess ? url : "/forbidden"}
      state={
        hasAccess
          ? undefined
          : {
              message: "You don’t have permission to access property"
            }
      }
      className="p-6 bg-white hover:bg-alpha-40 cursor-pointer flex items-center w-full"
    >
      <div className="space-y-4 w-full">
        <div className="flex justify-between items-center w-full">
          {mentionTypeMap[type] ? (
            <div className="flex gap-2.5 items-center">
              <HugeiconsIcon
                icon={mentionTypeMap[type].icon}
                size={16}
                className="text-content-200"
              />
              <p className="text-content-300 font-medium flex items-center text-sm">
                {mentionTypeMap[type].title}{" "}
                {hasAccess ? (
                  <p className="ml-1">{subjectName}</p>
                ) : (
                  <>
                    {mentionTypeMap[type].format}
                    <HugeiconsIcon
                      icon={SquareLock01Icon}
                      className="size-4 text-content-100 mx-1"
                    />
                    <span className="text-content-100">{subjectName}</span>
                  </>
                )}
              </p>
            </div>
          ) : null}

          <p className="text-content-300 font-medium text-sm">
            {moment(createAt).calendar(undefined, {
              sameDay: "[Today at] h:mm A",
              lastDay: "[Yesterday at] h:mm A",
              lastWeek: "MMM DD, YYYY [at] h:mm A",
              sameElse: "MMM DD, YYYY [at] h:mm A"
            })}
          </p>
        </div>

        <div className="flex gap-6 items-center">
          <span
            className={cx(
              "size-3 rounded-full shrink-0",
              Boolean(readAt) ? "bg-alpha-40" : "bg-primary-500"
            )}
          />
          <Avatar name={authorName} size={48} className="font-bold" />
          <div className="space-y-1">
            <p className="text-base font-bold">{authorName}</p>
            <LiteTextEditor
              id="notification-card-rich-text"
              editable={false}
              initialValue={richText?.document_html}
              containerClassName="text-content-400 text-base"
            />
          </div>
        </div>
      </div>
    </Link>
  );
};
